import { useRef, useState, useEffect } from 'react'
import { GripVertical, X } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Checkbox } from '@/components/ui/Checkbox'
import type { SubTask } from '@/lib/types'

interface SubtaskItemProps {
  subtask: SubTask
  onToggle: (id: string) => void
  onUpdate: (id: string, title: string) => void
  onDelete: (id: string) => void
  sortable?: boolean
}

export function SubtaskItem({ subtask, onToggle, onUpdate, onDelete, sortable = false }: SubtaskItemProps) {
  const [isEditing, setIsEditing] = useState(!subtask.title)
  const [editValue, setEditValue] = useState(subtask.title)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: subtask.id,
    disabled: !sortable,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleSave = () => {
    const trimmed = editValue.trim()
    if (trimmed) {
      onUpdate(subtask.id, trimmed)
    } else {
      onDelete(subtask.id)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditValue(subtask.title)
      setIsEditing(false)
    } else if (e.key === 'Backspace' && editValue === '') {
      onDelete(subtask.id)
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 py-1.5 group ${isDragging ? 'opacity-50' : ''}`}
    >
      {/* Drag handle */}
      {sortable ? (
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical className="w-4 h-4 text-disabled" />
        </div>
      ) : (
        <GripVertical className="w-4 h-4 text-disabled flex-shrink-0 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />
      )}

      {/* Checkbox */}
      <Checkbox
        checked={subtask.isCompleted}
        onChange={() => onToggle(subtask.id)}
        size="sm"
      />

      {/* Title */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="flex-1 text-sm bg-transparent border-none outline-none text-heading placeholder:text-disabled"
          placeholder="하위 작업 입력"
        />
      ) : (
        <span
          onClick={() => setIsEditing(true)}
          className={`flex-1 text-sm cursor-text ${
            subtask.isCompleted
              ? 'line-through text-disabled'
              : 'text-heading'
          }`}
        >
          {subtask.title}
        </span>
      )}

      {/* Delete */}
      <button
        type="button"
        onClick={() => onDelete(subtask.id)}
        className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity text-disabled hover:text-red-500 hover-surface"
        aria-label="하위 작업 삭제"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
